<template>
  <div class="flex-1 overflow-hidden">
    <nav class="relative flex h-[50px] w-full overflow-hidden">
      <button
        v-if="!isLeftMax"
        class="absolute left-0 top-0 flex h-[50px] w-[50px] items-center justify-center border-x-0 border-b-4 border-t-0 border-solid border-transparent bg-white/50"
        @click="handleScrollTo('left')"
      >
        <IconCaret direction="left" class="text-blue-400" />
      </button>

      <ul
        ref="menuRef"
        class="scrollbar-h-px flex w-full items-center overflow-y-hidden overflow-x-scroll"
      >
        <li v-for="(item, index) in menus" :key="hash(item)" class="px-2.5">
          <TheNuxtLink
            v-if="item.type === EnumMenuType.LINK && item.link"
            :to="
              isCN
                ? `/${locale}/marketplace${item.link?.url}`
                : `/${locale}${item.link?.url}`
            "
            :title="item.name"
            :data-test="`navigation__menu-${item.name}`"
            :class="!isMenuOpen && 'is-close-submenu'"
            :target="
              item.link?.target === EnumLinkTarget.BLANK ? '_blank' : '_self'
            "
            active-class="nuxt-link-active"
            class="menu-link fixed-bold-width"
            @click="menuStore.HANDLE_CLOSE_MENU()"
          >
            {{ item.name }}
          </TheNuxtLink>
          <!-- TODO: 待 CN 選單確定改版後移除 -->
          <button
            v-if="isCN && item.type === EnumMenuType.DROPDOWN"
            :title="item.name"
            :data-test="`navigation__menu-${item.name}`"
            :class="isMenuOpen && currentMenu === index && 'menu-link-active'"
            class="menu-link fixed-bold-width"
            @click="handleCNClick(index)"
          >
            {{ item.name }}
          </button>
          <button
            v-if="!isCN && item.type === EnumMenuType.DROPDOWN"
            :title="item.name"
            :data-test="`navigation__menu-${item.name}`"
            :class="isMenuOpen && currentMenu === index && 'menu-link-active'"
            class="menu-link fixed-bold-width"
            @click="handleClick(index)"
          >
            {{ item.name }}
          </button>
        </li>
      </ul>

      <button
        v-if="!isRightMax"
        class="absolute right-0 top-0 flex h-[50px] w-[50px] items-center justify-center border-x-0 border-b-4 border-t-0 border-solid border-transparent bg-white/50"
        @click="handleScrollTo('right')"
      >
        <IconCaret direction="right" class="text-blue-400" />
      </button>
    </nav>
  </div>
</template>

<script setup lang="ts">
  import { useScroll, useWindowSize } from '@vueuse/core';
  import hash from 'object-hash';

  import { EnumLinkTarget, EnumMenuType } from '@/assets/types/api/Menus';
  import { EnumCNMenuIndex } from '@/stores/menuStore';

  const route = useRoute();
  const { locale } = useI18n();
  const { isCN } = useLanguage();
  const menuRef = useTemplateRef<HTMLElement>('menuRef');
  const { x, isScrolling } = useScroll(menuRef, { behavior: 'smooth' });
  const { width } = useWindowSize();
  const menuStore = useMenuStore();
  const { menus, currentMenu, isMenuOpen } = storeToRefs(menuStore);

  const isLeftMax = ref<boolean>(true);
  const isRightMax = ref<boolean>(true);

  const handleClick = (index: number): void => {
    switch (index) {
      case 1:
        menuStore.isMenuOpen = true;
        menuStore.currentMenu = EnumMenuIndex.PRODUCTS;
        break;
      case 2:
        menuStore.isMenuOpen = true;
        menuStore.currentMenu = EnumMenuIndex.SOLUTIONS;
        break;
      case 3:
        menuStore.isMenuOpen = true;
        menuStore.currentMenu = EnumMenuIndex.TOPICS;
        break;
      case 4:
        menuStore.isMenuOpen = true;
        menuStore.currentMenu = EnumMenuIndex.ECOSYSTEM;
        break;
      case 5:
        menuStore.isMenuOpen = true;
        menuStore.currentMenu = EnumMenuIndex.RESOURCES;
        break;
      default:
        menuStore.HANDLE_CLOSE_MENU();
        break;
    }
  };

  // TODO: 待 CN 選單確定改版後移除
  const handleCNClick = (index: number): void => {
    switch (index) {
      case 0:
        menuStore.isMenuOpen = true;
        menuStore.currentMenu = EnumCNMenuIndex.SOLUTIONS;
        break;
      case 1:
        menuStore.isMenuOpen = true;
        menuStore.currentMenu = EnumCNMenuIndex.PRODUCTS;
        break;
      case 2:
        menuStore.isMenuOpen = true;
        menuStore.currentMenu = EnumCNMenuIndex.RESOURCES;
        break;
      default:
        menuStore.HANDLE_CLOSE_MENU();
        break;
    }
  };

  const handleScrollTo = (direction: 'left' | 'right'): void => {
    if (direction === 'left') {
      x.value = 0;
    }
    if (direction === 'right') {
      x.value = menuRef.value ? menuRef.value.scrollWidth : 100;
    }
    checkHasScrollArrow();
  };

  const checkHasScrollArrow = (): void => {
    nextTick(() => {
      if (menuRef.value) {
        isLeftMax.value = menuRef.value.scrollLeft <= 10;
        isRightMax.value =
          menuRef.value.scrollLeft >=
          menuRef.value.scrollWidth - menuRef.value.clientWidth - 10;
      }
    });
  };

  watch(width, () => checkHasScrollArrow());

  watch(route, () => checkHasScrollArrow(), { deep: true });

  watch(menus, () => checkHasScrollArrow(), { deep: true });

  watch(isScrolling, (value: boolean) => {
    if (!value) {
      checkHasScrollArrow();
    }
  });

  onMounted(() => checkHasScrollArrow());
</script>

<style lang="scss" scoped>
  .menu-link {
    @apply block whitespace-nowrap border-x-0 border-b-4 border-t-0 border-solid border-white py-2 text-center text-gray-600 hover:font-bold hover:text-blue-400;
  }

  .menu-link-active,
  .nuxt-link-active.is-close-submenu {
    @apply border-blue-400 font-bold;
  }

  .fixed-bold-width {
    @apply before:invisible before:block before:h-px before:overflow-hidden before:font-bold before:text-transparent before:content-[attr(title)];
  }
</style>
